/**
 * Original single-chapter orchestration semantics vendored from:
 *   original-golden-master/App.tsx runOrchestrationLoop
 */
import type {
  Chapter,
  ChapterPlan,
  EditorResult,
  Ledger,
  PublicationEditorResult,
  SoftStateBrief,
  StoryBible,
  ValidationResult,
} from '../../types';
import {
  editChapter,
  generateChapter,
  publishChapter,
  updateLedger,
  validateChapter,
} from './gemini.agents';

export const MAX_EDITOR_RETRIES = 2;
export const ULTIMATE_DEADLOCK_RETRY = 8;

export interface OriginalChapterOrchestrationInput {
  storyBible: StoryBible;
  ledger: Ledger;
  chapterNumber: number;
  previousChapters: Chapter[];
  directorsNote?: string;
  correctionOrder?: string;
  failedDraft?: string;
  retryCount?: number;
  softState?: SoftStateBrief | null;
  mode?: StoryBible['mode'];
}

export interface OriginalChapterOrchestrationResult {
  chapter: Chapter;
  ledger: Ledger;
  inspectorResult: ValidationResult;
  editorResult: EditorResult;
  publicationResult: PublicationEditorResult;
  correctionCount: number;
  editorRetryCount: number;
  nextChapterNumber: number;
}

function fallbackPlan(chapterNumber: number): ChapterPlan {
  return {
    chapterNumber,
    title: `Chapter ${chapterNumber}`,
    summary: 'Advance the plot.',
    requiredBeats: [],
    status: 'planned',
    id: 'fallback',
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Runs one successful original-engine chapter pass:
 * Scribe → Inspector (pass) → Editor → Publication Editor → Archivist.
 * Does not invoke experimental gates between Archivist and return.
 */
export async function runOriginalChapterOrchestration(
  input: OriginalChapterOrchestrationInput,
): Promise<OriginalChapterOrchestrationResult> {
  const {
    storyBible,
    ledger: initialLedger,
    chapterNumber,
    previousChapters,
    directorsNote,
    correctionOrder,
    failedDraft,
    retryCount = 0,
    softState = null,
  } = input;

  const bible = storyBible;
  const plan = bible.chapterPlans?.find((p) => p?.chapterNumber === chapterNumber) || fallbackPlan(chapterNumber);
  const prevSummary =
    previousChapters.length > 0
      ? previousChapters[previousChapters.length - 1].summary
      : 'The story begins.';

  let correctionCount = correctionOrder ? 1 : 0;
  let editorRetries = 0;
  let finalDraft: Chapter | null = null;
  let editorFeedback: string | undefined;
  let lastInspector: ValidationResult | null = null;
  let lastEditor: EditorResult | null = null;

  while (editorRetries <= MAX_EDITOR_RETRIES) {
    const currentDraft = await generateChapter(
      bible,
      initialLedger,
      chapterNumber,
      prevSummary,
      correctionOrder,
      editorFeedback,
      directorsNote,
      retryCount,
      failedDraft,
      softState,
    );

    const validation = await validateChapter(
      currentDraft,
      initialLedger,
      bible,
      directorsNote,
      retryCount,
      plan,
    );
    lastInspector = validation;

    if (validation.status === 'FAIL') {
      correctionCount += 1;
      throw new OriginalChapterOrchestrationError('INSPECTOR_FAIL', validation.reason || 'Inspector rejected draft', {
        inspectorResult: validation,
        correctionCount,
      });
    }

    const editorResult = await editChapter(currentDraft, bible, initialLedger, plan);
    lastEditor = editorResult;

    if (editorResult.status === 'APPROVED' || editorRetries === MAX_EDITOR_RETRIES) {
      finalDraft = {
        ...currentDraft,
        content: editorResult.polished_text || currentDraft.content,
      };
      break;
    }

    editorFeedback = editorResult.feedback;
    editorRetries += 1;
  }

  if (!finalDraft || !lastInspector || !lastEditor) {
    throw new Error('Original chapter orchestration failed to produce a final draft.');
  }

  const publishResult = await publishChapter(finalDraft, bible, initialLedger, plan);
  const publishedChapter: Chapter = {
    ...finalDraft,
    content: publishResult.patched_content,
  };

  const updatedLedger = await updateLedger(publishedChapter, initialLedger, bible.mode);

  return {
    chapter: publishedChapter,
    ledger: updatedLedger,
    inspectorResult: lastInspector,
    editorResult: lastEditor,
    publicationResult: publishResult,
    correctionCount,
    editorRetryCount: editorRetries,
    nextChapterNumber: chapterNumber + 1,
  };
}

export class OriginalChapterOrchestrationError extends Error {
  readonly code: string;
  readonly details?: Record<string, unknown>;

  constructor(code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'OriginalChapterOrchestrationError';
    this.code = code;
    this.details = details;
  }
}
